import { collection, deleteDoc, doc, onSnapshot, query, serverTimestamp, setDoc, updateDoc, where } from 'firebase/firestore';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { DuoBoost } from '../components/DuoBoost';
import { TaskSheet, nextQuarter, type Draft } from '../components/TaskSheet';
import { db } from '../firebase';
import { phaseOn, type CycleData } from '../lib/cycle';
import { loadCycle, syncPhase } from '../lib/cycleSync';
import { addDays, dow, endTime, nowMinutes, prettyDate, toMinutes, today } from '../lib/dates';
import { habitHintText, habitTitleText, mealName, mealNote, portionText } from '../lib/mealText';
import { dayTotals } from '../lib/plan';
import { isAssigned, occursOn } from '../lib/recurrence';
import { setSelectedDay } from '../lib/selectedDay';
import { waterShare } from '../lib/targets';
import type { Member, PlanDay, PlanMeal, Task } from '../lib/types';
import { useApp } from '../state';
import { locale, t } from '../lib/i18n';

interface Item {
  key: string;
  time: string;
  dur: number;
  icon: string;
  title: string;
  kind: string;
  mine: boolean;
  forPartner: boolean;
  partnerHas: boolean;
  meal?: PlanMeal;
  task?: Task;
  hint?: string;
}

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const KINDS = new Set(['meal', 'water', 'walk', 'workout', 'supplement', 'together']);

function fillHint(hint: string | undefined, m: Member | null): string | undefined {
  if (!hint) return hint;
  return hint.replace('{waterShare}', waterShare(m?.targets)).replace('{steps}', (m?.targets?.steps ?? 8000).toLocaleString());
}

function fmtDur(min: number): string {
  if (min < 60) return `${min} ${t('min')}`;
  const h = Math.floor(min / 60), m = min % 60;
  return m ? `${h} ${t('h')} ${m} ${t('min')}` : `${h} ${t('h')}`;
}

const mondayOf = (d: string) => addDays(d, -((dow(d) + 6) % 7));

export default function Today() {
  const { user, profile, duo, tasks, me, partner } = useApp();
  const uid = user!.uid;
  const [date, setDate] = useState(today());
  const [follow, setFollow] = useState(true); // stay on "today", even across midnight, until they browse away
  const [nowMin, setNowMin] = useState(nowMinutes());
  const go = (d: string) => { setDate(d); setFollow(d === today()); };
  useEffect(() => { setSelectedDay(date); }, [date]);
  useEffect(() => {
    const tick = () => {
      setNowMin(nowMinutes());
      if (follow) setDate((d) => (d === today() ? d : today()));
    };
    const id = setInterval(tick, 30_000);
    document.addEventListener('visibilitychange', tick);
    return () => { clearInterval(id); document.removeEventListener('visibilitychange', tick); };
  }, [follow]);

  const [day, setDay] = useState<PlanDay | null | undefined>(undefined);
  const [weekDays, setWeekDays] = useState<Record<string, PlanDay>>({});
  const [done, setDone] = useState<Set<string>>(new Set());
  const [cycle, setCycle] = useState<CycleData | null>(null);
  const [editing, setEditing] = useState<{ id: string; draft: Draft } | null>(null);
  const [replanning, setReplanning] = useState(false);
  const monday = mondayOf(date);

  useEffect(() => {
    if (!duo) return;
    setDay(undefined);
    return onSnapshot(doc(db, 'duos', duo.id, 'planDays', date), (s) => setDay(s.exists() ? (s.data() as PlanDay) : null));
  }, [duo, date]);

  // The whole visible week, for the little dots under each day.
  useEffect(() => {
    if (!duo) return;
    const q = query(collection(db, 'duos', duo.id, 'planDays'), where('date', '>=', monday), where('date', '<=', addDays(monday, 6)));
    return onSnapshot(q, (s) => setWeekDays(Object.fromEntries(s.docs.map((d) => [d.id, d.data() as PlanDay]))), () => setWeekDays({}));
  }, [duo, monday]);

  useEffect(() => {
    if (!duo) return;
    const q = query(collection(db, 'duos', duo.id, 'completions'), where('date', '==', date));
    return onSnapshot(q, (s) => setDone(new Set(s.docs.map((d) => `${d.data().itemKey}__${d.data().uid}`))));
  }, [duo, date]);

  useEffect(() => {
    if (!profile?.cycleTracking) { setCycle(null); return; }
    loadCycle(uid).then((c) => {
      setCycle(c);
      // Refresh the phase the partner sees once a day, when the app is opened.
      if (profile.shareCyclePhase && me?.cyclePhase?.updatedAt !== today()) void syncPhase(uid, duo?.id, c, true);
    });
  }, [uid, profile?.cycleTracking, profile?.shareCyclePhase, duo?.id, me?.cyclePhase?.updatedAt]);

  const wake = profile?.day?.wake ?? '06:30';
  const windDown = profile?.day?.windDown ?? '22:00';

  const items = useMemo<Item[]>(() => {
    const list: Item[] = [];
    list.push({ key: 'wake', time: wake, dur: 0, icon: '⏰', title: t('Rise and shine'), kind: 'wake', mine: true, forPartner: false, partnerHas: false });
    for (const m of day?.meals ?? []) {
      const partnerHas = Boolean(partner && m.portions[partner.uid]);
      list.push({ key: m.key, time: m.time, dur: m.dur, icon: m.icon, title: mealName(m.name), kind: m.cheat ? 'cheat' : 'meal', mine: Boolean(m.portions[uid]), forPartner: false, partnerHas, meal: m, hint: mealNote(m.note) });
    }
    for (const t of tasks as Task[]) {
      if (!occursOn(t, date)) continue;
      const mine = isAssigned(t, uid);
      const partnerHas = Boolean(partner && isAssigned(t, partner.uid));
      const forPartner = !mine && partnerHas;
      if (!mine && !forPartner) continue;
      const kind = t.kind && KINDS.has(t.kind) ? t.kind : 'custom';
      list.push({ key: `task-${t.id}`, time: t.time, dur: t.durationMin ?? 10, icon: t.icon ?? '⏰', title: habitTitleText(t.title), kind, mine, forPartner, partnerHas, task: t, hint: fillHint(habitHintText(t.hint), me) });
    }
    list.push({ key: 'wind', time: windDown, dur: 0, icon: '🌙', title: t('Wind down'), kind: 'night', mine: true, forPartner: false, partnerHas: false });
    return list.sort((a, b) => a.time.localeCompare(b.time) || a.dur - b.dur);
  }, [day, tasks, date, uid, partner, me, wake, windDown]);

  const isToday = date === today();
  const isPast = date < today();
  const counted = items.filter((i) => i.mine && i.kind !== 'wake' && i.kind !== 'night');
  const myDone = counted.filter((i) => done.has(`${i.key}__${uid}`)).length;
  const partnerItems = partner ? items.filter((i) => i.partnerHas) : [];
  const partnerDone = partner ? partnerItems.filter((i) => done.has(`${i.key}__${partner.uid}`)).length : 0;
  const ended = (i: Item) => isPast || (isToday && toMinutes(i.time) + i.dur <= nowMin);
  const nextKey = isToday ? items.find((i) => i.mine && !done.has(`${i.key}__${uid}`) && toMinutes(i.time) + i.dur >= nowMin)?.key : undefined;
  const missed = isToday ? items.filter((i) => i.mine && i.task?.repeat.type === 'once' && !done.has(`${i.key}__${uid}`) && toMinutes(i.time) + i.dur < nowMin) : [];

  const phase = cycle ? phaseOn(cycle, date) : null;
  const totals = day ? dayTotals(day, uid) : null;
  const kcalAllowance = phase?.info.kcalAdjust ?? 0;

  async function toggle(item: Item) {
    if (!duo || !item.mine) return;
    const id = `${date}__${item.key}__${uid}`;
    const ref = doc(db, 'duos', duo.id, 'completions', id);
    if (done.has(`${item.key}__${uid}`)) await deleteDoc(ref);
    else await setDoc(ref, { date, itemKey: item.key, uid, doneAt: serverTimestamp() });
  }

  /** Replan: move missed one-off tasks to the next free quarter hours, one after another. */
  async function replan() {
    if (!duo) return;
    setReplanning(true);
    let t = toMinutes(nextQuarter());
    for (const m of missed) {
      if (t > 23 * 60 + 45) break;
      const hhmm = `${String(Math.floor(t / 60)).padStart(2, '0')}:${String(t % 60).padStart(2, '0')}`;
      await updateDoc(doc(db, 'duos', duo.id, 'tasks', m.task!.id), { time: hhmm });
      t += Math.ceil((m.dur + 5) / 15) * 15;
    }
    setReplanning(false);
  }

  // Swipe the week strip left/right to change week.
  const swipe = useRef<number | null>(null);
  const weekDates = Array.from({ length: 7 }, (_, i) => addDays(monday, i));
  const monthLabel = new Date(date + 'T12:00:00').toLocaleDateString(locale(), { month: 'long' });

  // Timeline rows: items with free-time gaps and the "now" line woven in.
  const rows: ReactNode[] = [];
  let nowPlaced = !isToday;
  items.forEach((i, idx) => {
    const start = toMinutes(i.time);
    if (!nowPlaced && start > nowMin) {
      rows.push(<li key="now" className="tl-now" aria-label={t('Now')}><b>{endTime('00:00', nowMin)}</b><i /></li>);
      nowPlaced = true;
    }
    const mineDone = done.has(`${i.key}__${uid}`);
    const pDone = partner ? done.has(`${i.key}__${partner.uid}`) : false;
    const mp = i.meal?.portions[uid];
    const pp = partner ? i.meal?.portions[partner.uid] : undefined;
    const tall = 54 + Math.min(Math.max(i.dur - 15, 0), 105) * 0.6;
    const cls = ['tl-item', `k-${i.kind}`, mineDone && 'done', i.key === nextKey && 'next', i.forPartner && 'theirs', ended(i) && 'past'].filter(Boolean).join(' ');
    rows.push(
      <li key={i.key} className={cls} style={{ animationDelay: `${Math.min(idx, 12) * 30}ms` }}>
        <span className="tl-time">{i.time}</span>
        <span className="tl-node"><span className="bubble" style={{ minHeight: tall }} aria-hidden>{i.icon}</span></span>
        <div className="tl-body" onClick={() => i.task && setEditing({ id: i.task.id, draft: { remind: true, hint: '', durationMin: 15, ...withoutId(i.task) } })}>
          <span className="tl-range">{i.dur ? `${i.time} – ${endTime(i.time, i.dur)} (${fmtDur(i.dur)})` : i.time}{i.task && i.task.repeat.type !== 'once' ? ' ↻' : ''}</span>
          <span className="tl-title">{i.meal && !i.title.toLowerCase().startsWith(i.meal.kind.toLowerCase()) ? `${t(i.meal.kind)}: ${i.title}` : i.title}</span>
          {i.forPartner && <span className="tl-who">{t('For {name}', { name: partner?.displayName ?? '' })}</span>}
          {mp && <p className="portion me"><b>{t('You')} · ~{mp.k} kcal · {mp.p} {t('g protein')}</b>{portionText(mp.d)}</p>}
          {pp && <details className="portion partner" onClick={(e) => e.stopPropagation()}><summary>{partner?.displayName} · ~{pp.k} kcal</summary>{portionText(pp.d)}</details>}
          {i.hint && <p className="hint">{i.hint}</p>}
        </div>
        <span className="tl-checks">
          {i.mine && (
            <button className={`tick${mineDone ? ' on' : ''}`} aria-pressed={mineDone} aria-label={t('Mark {title} done', { title: i.title })} onClick={() => toggle(i)}>
              <svg viewBox="0 0 16 16" aria-hidden><path d="M3.5 8.5l3 3 6-7" /></svg>
            </button>
          )}
          {partner && i.partnerHas && (
            <span className={`partner-dot${pDone ? ' on' : ''}`} title={t(pDone ? '{name} did it' : '{name} not yet', { name: partner.displayName })}>{partner.displayName.slice(0, 1)}</span>
          )}
        </span>
      </li>,
    );
    const next = items[idx + 1];
    if (next) {
      const free = toMinutes(next.time) - (start + i.dur);
      if (free >= 45) rows.push(<li key={`gap-${i.key}`} className="tl-gap"><span /><span className="tl-node" /><span>{t('{d} free', { d: fmtDur(free) })}{isToday && toMinutes(next.time) > nowMin && start + i.dur <= nowMin ? ` · ${t('a good moment for a walk')}` : ''}</span></li>);
    }
  });
  if (!nowPlaced) rows.push(<li key="now" className="tl-now" aria-label={t('Now')}><b>{endTime('00:00', nowMin)}</b><i /></li>);

  return (
    <main className="page">
      <header className="weekhead">
        <div className="row between">
          <button className="monthtitle" onClick={() => go(today())} aria-label={t('Back to today')}>
            {monthLabel} <span>{date.slice(0, 4)}</span>
            <svg viewBox="0 0 12 20" aria-hidden><path d="M2 2l8 8-8 8" /></svg>
          </button>
          <div className="weeknav">
            <button className="icon-btn" aria-label={t('Previous week')} onClick={() => go(addDays(date, -7))}>‹</button>
            <button className="icon-btn" aria-label={t('Next week')} onClick={() => go(addDays(date, 7))}>›</button>
          </div>
        </div>
        <span className="greet">{isToday ? `${greeting()}, ${profile?.displayName ?? ''}` : prettyDate(date)}</span>
        <div
          className="weekstrip"
          onPointerDown={(e) => { swipe.current = e.clientX; }}
          onPointerUp={(e) => {
            if (swipe.current === null) return;
            const dx = e.clientX - swipe.current;
            swipe.current = null;
            if (Math.abs(dx) > 50) go(addDays(date, dx < 0 ? 7 : -7));
          }}
        >
          {weekDates.map((d, i) => {
            const pd = weekDays[d];
            const cheat = pd?.meals.some((m) => m.cheat);
            const workout = (tasks as Task[]).some((t) => t.kind === 'workout' && occursOn(t, d) && isAssigned(t, uid));
            const together = (tasks as Task[]).some((t) => t.kind === 'together' && occursOn(t, d));
            return (
              <button key={d} className={`${d === date ? 'sel' : ''} ${d === today() ? 'is-today' : ''}`} aria-pressed={d === date} aria-label={prettyDate(d)} onClick={() => go(d)}>
                <span className="wd">{t(WEEKDAYS[i])}</span>
                <span className="dn">{Number(d.slice(8))}</span>
                <span className="dots">{pd && !cheat && <i />}{cheat && <i className="cheat" />}{workout && <i className="w-dot" />}{together && <i className="t-dot" />}</span>
              </button>
            );
          })}
        </div>
      </header>

      <section className="duo-chips">
        <Chip label={profile?.displayName ?? t('You')} done={myDone} total={counted.length} tone="me" />
        {partner && <Chip label={partner.displayName} done={partnerDone} total={partnerItems.length} tone="partner" />}
        {totals && <span className="duo-chip kcal"><b className="num">{(totals.k + kcalAllowance).toLocaleString()}</b> kcal · {totals.p} {t('g protein')}</span>}
      </section>

      {isToday && <DuoBoost />}

      {missed.length > 0 && (
        <section className="replan">
          <p>{t(missed.length === 1 ? '{n} missed task today. Move it to later?' : '{n} missed tasks today. Move them to later?', { n: missed.length })}</p>
          <button className="btn primary slim" onClick={replan} disabled={replanning}>{t('Replan')}</button>
        </section>
      )}

      {phase && (
        <section className={`phase phase-${phase.info.id}`}>
          <div className="row between"><b>{t(phase.info.label)} · {t('day {n}', { n: phase.day })}{kcalAllowance ? ` · +${kcalAllowance} kcal` : ''}</b><Link to="/cycle" className="small">{t('Cycle')}</Link></div>
          <p>{t(phase.info.food[0])}</p>
        </section>
      )}
      {partner?.cyclePhase && partner.cyclePhase.updatedAt >= addDays(today(), -2) && (
        <section className={`phase phase-${partner.cyclePhase.phase}`}>
          <b>{partner.displayName}: {partner.cyclePhase.label}</b>
          <p>{partner.cyclePhase.partnerHint}</p>
        </section>
      )}

      {day === null && (
        <section className="replan">
          <p>{t('No meal plan for this day yet.')}</p>
          <Link className="btn slim" to="/plan">{t('Plan')}</Link>
        </section>
      )}

      <ol className="tl">
        {rows}
      </ol>

      {editing && <TaskSheet id={editing.id} initial={editing.draft} onClose={() => setEditing(null)} />}
    </main>
  );
}

function withoutId(t: Task): Draft {
  const copy: Partial<Task> = { ...t };
  delete copy.id;
  return copy as Draft;
}

function greeting(): string {
  const h = new Date().getHours();
  return t(h < 5 ? 'Good night' : h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening');
}

function Chip({ label, done, total, tone }: { label: string; done: number; total: number; tone: 'me' | 'partner' }) {
  const pct = total ? Math.round((done / total) * 100) : 0;
  return (
    <span className="duo-chip">
      <svg viewBox="0 0 36 36" className={`ring ${tone}`} aria-hidden>
        <circle cx="18" cy="18" r="14" className="track" />
        {pct > 0 && <circle cx="18" cy="18" r="14" className="fill" strokeDasharray={`${(pct / 100) * 88} 88`} />}
      </svg>
      <span><b>{label}</b> <span className="num muted">{done}/{total}</span></span>
    </span>
  );
}
